import type { StarMetric } from '@full-elevator/sim';
import { formatDuration } from '@full-elevator/sim';
import { callButton, clear, el, icon } from './dom.js';
import { bonusLine } from './medal.js';
import type { LossCause } from './outcome.js';

/**
 * The results screen: the run's verdict, said with some ceremony.
 *
 * Its job is unchanged — answer "why did I get that?" — so the ceremony is built
 * around the answer rather than in front of it. The stars land one at a time,
 * the graded number counts up along a bar marked with the two- and three-star
 * lines, and the sentence explaining the result sits right under it. Everything
 * that used to be a table is still here, as tiles.
 *
 * None of it is allowed to slow a retry down: the buttons work from the first
 * frame, a tap anywhere settles every animation at once, and a phone set to
 * reduce motion never sees them.
 */
export interface ResultView {
  /** "SHIFT 31 · THE GRAND HOTEL" — where this was. */
  readonly shift: string;
  readonly name: string;
  readonly won: boolean;
  readonly stars: number;
  readonly metric: StarMetric;
  /** The graded number: milliseconds on a time level, points on a service one. */
  readonly value: number;
  readonly two: number;
  readonly three: number;
  readonly reason: string;
  /** A lost shift's cause, in a few words, said before anything else. */
  readonly cause?: LossCause | null;
  readonly tiles: readonly (readonly [string, string])[];
  readonly earned: number;
  readonly milestone: 'first-clear' | 'new-best' | null;
  /**
   * The garage, pointed at (ADR-0041): the first part's lesson, which pulses
   * Upgrade and leaves the words to the lesson, or a better lift where the
   * player is stuck, said in a bubble over it.
   */
  readonly nudge?: { readonly text: string; readonly lesson: boolean } | null;
  /** The shift's bonus goal (ADR-0040): met this run or not, and what meeting it paid. */
  readonly bonus?: { readonly text: string; readonly met: boolean; readonly coins: number } | null;
  /**
   * A building's last shift, won for the first time: the building is done, and
   * the next one opens (the owner, PLAYTEST_LOG 2026-10-28). Null on any other result.
   */
  readonly complete?: BuildingDone | null;
}

export interface BuildingDone {
  readonly building: string;
  readonly stars: number;
  readonly of: number;
  /** The building that opens next, or null after the last one. */
  readonly next: string | null;
}

export interface ResultActions {
  readonly retry: () => void;
  readonly levels: () => void;
  /** Straight to the garage: a part bought now is on the lift for the next shift. */
  readonly garage: () => void;
  readonly next: (() => void) | null;
  /** Called as each earned star lands, for its sound (v0.8). Never on a settled screen. */
  readonly starLanded?: (index: number) => void;
}

/** Star sounds still to come, so a tap that settles the screen can stop them. */
let starTimers: ReturnType<typeof setTimeout>[] = [];

export function stopStarSounds(): void {
  for (const timer of starTimers) clearTimeout(timer);
  starTimers = [];
}

/** When the first star lands, and how far apart the rest follow. Seconds. */
const FIRST_STAR = 0.45;
const STAR_GAP = 0.32;
/** How long the number takes to count up. Milliseconds. */
const COUNT_MS = 850;
const COUNT_DELAY_MS = 250;

/** Where the two- and three-star lines sit along the bar. */
const TWO_AT = 0.4;
const THREE_AT = 0.75;

/**
 * Where a result sits along the bar, 0–1, with the star lines at fixed places.
 *
 * Piecewise linear between the thresholds, so the marker crossing a line means
 * exactly what the stars say. The ends are a generous margin either side —
 * the bar shows standing, not a scale to read numbers off.
 */
export function barPosition(metric: StarMetric, value: number, two: number, three: number): number {
  // Turn both metrics into "higher is better" anchors.
  const anchors: [number, number][] =
    metric === 'service'
      ? [
          [0, 0],
          [two, TWO_AT],
          [three, THREE_AT],
          [three + (three - two) * 0.75, 1],
        ]
      : [
          [-(two + (two - three) * 1.5), 0],
          [-two, TWO_AT],
          [-three, THREE_AT],
          [-(three - (two - three) * 0.75), 1],
        ];
  const v = metric === 'service' ? value : -value;
  let x = v <= (anchors[0]?.[0] ?? 0) ? 0 : 1;
  for (let i = 1; i < anchors.length; i += 1) {
    const [v0, x0] = anchors[i - 1] as [number, number];
    const [v1, x1] = anchors[i] as [number, number];
    if (v >= v0 && v <= v1) {
      x = v1 === v0 ? x1 : x0 + ((v - v0) / (v1 - v0)) * (x1 - x0);
      break;
    }
  }
  return Math.min(0.98, Math.max(0.02, x));
}

/**
 * What the score card's number is: "Points", as the goal card and the HUD call
 * them. It said "Service", a fourth name for one idea (writer review, 2026-10-26).
 */
export function scoreLabel(metric: StarMetric): string {
  return metric === 'service' ? 'Points' : metric === 'trips' ? 'Trips' : 'Time';
}

function formatValue(metric: StarMetric, value: number): string {
  // Whole trips as it counts up: unrounded, office 12 showed a run of digits
  // (the owner, PLAYTEST_LOG 2026-10-28).
  if (metric === 'trips') {
    const trips = Math.round(value);
    return `${trips} ${trips === 1 ? 'trip' : 'trips'}`;
  }
  return metric === 'service' ? String(Math.round(value)) : formatDuration(value);
}

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function renderResults(
  body: HTMLElement,
  foot: HTMLElement,
  view: ResultView,
  actions: ResultActions,
): void {
  clear(body);
  clear(foot);
  body.dataset.won = String(view.won);
  body.dataset.stars = String(view.won ? view.stars : 0);
  delete body.dataset.settled;
  const earnedStars = view.won ? view.stars : 0;
  // Everything after the stars waits for the last one to land.
  body.style.setProperty('--after', `${FIRST_STAR + Math.max(1, earnedStars) * STAR_GAP + 0.2}s`);

  const inner = el('div', { class: 'results-inner' });
  inner.append(
    el('div', { class: 'results-head' }, [
      el('div', { class: 'results-shift', text: view.shift }),
      el('h2', { text: view.name }),
    ]),
    hero(view, earnedStars),
  );
  if (view.cause) inner.append(causePlate(view.cause));
  if (view.complete) inner.append(buildingDone(view.complete, view.stars === 3));

  let counter: HTMLElement | null = null;
  if (view.won) {
    const card = scoreCard(view);
    counter = card.counter;
    inner.append(card.root);
  }
  inner.append(el('p', { class: 'results-reason', text: view.reason }));
  if (view.bonus) inner.append(bonusResult(view.bonus));
  if (view.earned > 0) inner.append(coinPill(view.earned));
  inner.append(tiles(view.tiles));
  body.append(inner);

  const settle = startCountUp(counter, view);
  stopStarSounds();
  // A tap anywhere settles the ceremony, so a player who has seen it before is
  // never made to sit through it.
  body.onpointerdown = () => {
    body.dataset.settled = 'true';
    settle();
    stopStarSounds();
  };
  if (reducedMotion()) {
    body.dataset.settled = 'true';
    settle();
  } else if (actions.starLanded) {
    // Each star's sound lands with its pop, a beat after the star starts to grow.
    for (let i = 0; i < earnedStars; i += 1) {
      const land = actions.starLanded;
      starTimers.push(setTimeout(() => land(i), (FIRST_STAR + i * STAR_GAP) * 1000 + 180));
    }
  }

  foot.append(...footButtons(view, actions));
}

/**
 * The way on, along the bottom: back to the building, retry, the garage, and
 * the one thing most players want next as the big button — the next shift
 * after a win, another go after a loss (or after the last shift there is).
 */
function footButtons(view: ResultView, actions: ResultActions): HTMLElement[] {
  const buttons: HTMLElement[] = [callButton('home', 'Main menu', actions.levels)];
  if (actions.next) buttons.push(callButton('retry', 'Retry', actions.retry));

  const garage = el('button', { class: 'brass-pill', type: 'button' }, [
    icon('wrench'),
    el('span', { text: 'Upgrade' }),
  ]);
  garage.addEventListener('click', actions.garage);
  if (view.nudge) {
    // The first part's lesson says its own words, over a dimmed screen (ADR-0041).
    garage.dataset.nudge = view.nudge.lesson ? 'lesson' : 'hint';
    if (!view.nudge.lesson) {
      garage.append(el('span', { class: 'garage-nudge', role: 'status', text: view.nudge.text }));
    }
  }
  buttons.push(garage);

  // Out of a finished building, the big button names where it goes, without its
  // article, as the roof sign does: "On to The Grand Hotel" did not fit a phone.
  const onward = view.complete?.next ? view.complete.next.replace(/^The /, '') : 'Next shift';
  const primary = actions.next
    ? el('button', { class: 'action-pill', type: 'button' }, [onward, icon('play')])
    : el('button', { class: 'action-pill', type: 'button' }, [
        icon('retry'),
        view.won ? 'Play again' : 'Try again',
      ]);
  primary.addEventListener('click', actions.next ?? actions.retry);
  buttons.push(primary);
  return buttons;
}

/** Why it was lost, first: an icon and a few words on a plate (PLAYTEST_LOG 2026-10-30). */
function causePlate(cause: LossCause): HTMLElement {
  return el('div', { class: 'results-cause', role: 'status' }, [
    el('span', { class: 'results-cause-icon' }, [icon(cause.icon)]),
    el('span', { class: 'results-cause-text', text: cause.text }),
    ...(cause.figure ? [el('span', { class: 'results-cause-figure', text: cause.figure })] : []),
  ]);
}

function hero(view: ResultView, earned: number): HTMLElement {
  const verdict = !view.won
    ? 'Shift failed'
    : view.stars === 3
      ? 'Perfect shift!'
      : 'Shift cleared';
  const row = el('div', { class: 'results-starrow' });
  for (let i = 0; i < 3; i += 1) row.append(starSlot(i, i < earned));

  const root = el('div', { class: 'results-hero' }, [
    el('div', { class: 'results-rays', 'aria-hidden': 'true' }),
    row,
    el('div', { class: 'results-verdict', text: verdict }),
  ]);
  if (view.won && view.stars === 3) root.append(confetti());
  root.setAttribute('aria-label', `${verdict} ${earned} of 3 stars`);
  return root;
}

const SVG = 'http://www.w3.org/2000/svg';

/** A five-pointed star with rounded corners, as an SVG path on a 100-unit box. */
function starPath(): string {
  const points: string[] = [];
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? 46 : 21;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    points.push(`${(50 + r * Math.cos(a)).toFixed(1)} ${(54 + r * Math.sin(a)).toFixed(1)}`);
  }
  return `M${points.join(' L')} Z`;
}

const STAR = starPath();

function starSvg(className: string): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('class', className);
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML =
    `<path d="${STAR}" class="star-body"/>` +
    // A softer star inside, offset up-left: the light catching the top face.
    `<path d="${STAR}" class="star-face" transform="translate(13 9) scale(0.7)"/>`;
  return svg;
}

function starSlot(index: number, earned: boolean): HTMLElement {
  const slot = el('span', {
    class: 'result-star',
    'data-earned': String(earned),
    style: `--i:${index}; --at:${FIRST_STAR + index * STAR_GAP}s`,
  });
  slot.append(starSvg('star-empty'));
  if (!earned) return slot;

  slot.append(el('span', { class: 'star-ring' }), starSvg('star-gold'));
  const burst = el('span', { class: 'star-burst', 'aria-hidden': 'true' });
  // Ten sparks, spread evenly with a little jitter so no two stars burst alike.
  for (let k = 0; k < 10; k += 1) {
    const angle = k * 36 + ((index * 17 + k * 29) % 18);
    const distance = 44 + ((k * 13 + index * 7) % 26);
    const size = 7 + ((k * 5 + index) % 7);
    burst.append(
      el('span', {
        class: k % 3 === 0 ? 'spark spark-dot' : 'spark',
        style: `--a:${angle}deg; --d:${distance}px; --s:${size}px; --k:${(k % 4) * 0.03}s`,
      }),
    );
  }
  // One spark that stays: a slow twinkle on the star's upper tip.
  slot.append(burst, el('span', { class: 'star-twinkle', 'aria-hidden': 'true' }));
  return slot;
}

/** The building is done: its name, its stars, and what opens next, with confetti. */
function buildingDone(done: BuildingDone, confettiShown: boolean): HTMLElement {
  return el('div', { class: 'results-complete', role: 'status' }, [
    el('div', { class: 'results-complete-kicker', text: 'Building complete' }),
    el('div', { class: 'results-complete-name', text: done.building }),
    el('div', { class: 'results-complete-line', text: `${done.stars} of ${done.of} ★` }),
    el('div', {
      class: 'results-complete-line',
      text: done.next
        ? `${done.next} opens next.`
        : 'Every building is done. Thank you for playing.',
    }),
    // The three-star hero already rains confetti; a building done always does.
    ...(confettiShown ? [] : [confetti()]),
  ]);
}

function confetti(): HTMLElement {
  // Floor hues: the game's own colours, raining on its best result.
  const hues = [
    '#2d6a9c',
    '#3c7a2c',
    '#9a660b',
    '#a8406c',
    '#6a4ea6',
    '#b13f22',
    '#147373',
    '#f6c344',
  ];
  const root = el('div', { class: 'results-confetti', 'aria-hidden': 'true' });
  for (let i = 0; i < 22; i += 1) {
    root.append(
      el('span', {
        style:
          `--x:${(i * 47) % 100}%; --c:${hues[i % hues.length]}; ` +
          `--r:${(i * 73) % 360}deg; --f:${0.9 + ((i * 7) % 6) / 10}s; --k:${((i * 11) % 9) / 20}s`,
      }),
    );
  }
  return root;
}

function scoreCard(view: ResultView): { root: HTMLElement; counter: HTMLElement } {
  const counter = el('span', { class: 'results-value', text: formatValue(view.metric, 0) });
  const x = barPosition(view.metric, view.value, view.two, view.three);
  const bar = el('div', { class: 'results-bar', style: `--x:${(x * 100).toFixed(1)}%` }, [
    el('span', { class: 'results-zone' }),
    el('span', { class: 'results-tick', style: `--at:${TWO_AT * 100}%` }),
    el('span', { class: 'results-tick', style: `--at:${THREE_AT * 100}%` }),
    el('span', { class: 'results-marker' }),
  ]);
  const legend = el('div', { class: 'results-legend' }, [
    el('span', {
      style: `--at:${TWO_AT * 100}%`,
      text: `★★ ${formatValue(view.metric, view.two)}`,
    }),
    el('span', {
      style: `--at:${THREE_AT * 100}%`,
      text: `★★★ ${formatValue(view.metric, view.three)}`,
    }),
  ]);
  const root = el('div', { class: 'results-score' }, [
    el('div', {
      class: 'results-score-label',
      text: scoreLabel(view.metric),
    }),
    counter,
    bar,
    legend,
  ]);
  if (view.milestone) {
    root.append(
      el('div', {
        class: 'results-stamp',
        text: view.milestone === 'new-best' ? 'New best!' : 'First clear!',
      }),
    );
  }
  return { root, counter };
}

/**
 * The bonus goal, lit if this run met it: "Bonus · Lose nobody · +14" the
 * first time, "Met" after that, "Missed" otherwise.
 */
function bonusResult(bonus: NonNullable<ResultView['bonus']>): HTMLElement {
  const row = bonusLine(bonus.text, 0, bonus.met);
  row.classList.add('results-bonus');
  row.dataset.met = String(bonus.met);
  const verdict = !bonus.met ? 'Missed' : bonus.coins > 0 ? `+${bonus.coins}` : 'Met';
  row.append(el('span', { class: 'bonus-verdict', text: verdict }));
  return row;
}

function coinPill(earned: number): HTMLElement {
  const coin = document.createElementNS(SVG, 'svg');
  coin.setAttribute('viewBox', '0 0 20 20');
  coin.setAttribute('aria-hidden', 'true');
  coin.innerHTML =
    '<circle cx="10" cy="10" r="8" fill="#e6c98a" stroke="#875a06" stroke-width="2"/>' +
    '<path d="M10 6v8" stroke="#875a06" stroke-width="2" stroke-linecap="round"/>';
  return el('div', { class: 'results-coins' }, [coin, `+${earned} coins`]);
}

function tiles(rows: ResultView['tiles']): HTMLElement {
  const grid = el('dl', { class: 'results-tiles' });
  for (const [label, value] of rows) {
    // A long value (the fitted lift's parts) takes the whole row.
    const wide = value.length > 14;
    grid.append(
      el('div', { class: wide ? 'results-tile wide' : 'results-tile' }, [
        el('dt', { text: label }),
        el('dd', { text: value }),
      ]),
    );
  }
  return grid;
}

/** Counts the graded number up from zero. Returns a function that jumps to the end. */
function startCountUp(counter: HTMLElement | null, view: ResultView): () => void {
  if (!counter) return () => {};
  let done = false;
  const finish = (): void => {
    done = true;
    counter.textContent = formatValue(view.metric, view.value);
  };
  const started = performance.now() + COUNT_DELAY_MS;
  const tick = (now: number): void => {
    if (done || !counter.isConnected) return;
    const t = Math.min(1, Math.max(0, (now - started) / COUNT_MS));
    const eased = 1 - (1 - t) ** 3;
    counter.textContent = formatValue(view.metric, view.value * eased);
    if (t < 1) requestAnimationFrame(tick);
    else finish();
  };
  requestAnimationFrame(tick);
  return finish;
}
